import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  calculateUtilityCostPerDishSummary,
  type PerDishMetrics,
} from '../../utils/perDishCalculations';
import { Card } from './Card';
import { LoadingSpinner } from './LoadingSpinner';

interface UtilityCostPerDishSectionProps {
  data: PerDishMetrics[];
  currency: string;
  loading?: boolean;
  error?: string;
  missingCostRecords?: number;
  excludedCurrencyRecords?: number;
}

type UtilityCostKey = 'electricity' | 'water' | 'gas';

interface UtilityConfig {
  key: UtilityCostKey;
  label: string;
  color: string;
}

interface CostTrendPoint {
  date: string;
  formattedDate: string;
  electricity: number | null;
  water: number | null;
  gas: number | null;
}

const utilityConfigs: readonly UtilityConfig[] = [
  {
    key: 'electricity',
    label: 'Electricidad',
    color: 'var(--brand-600)',
  },
  {
    key: 'gas',
    label: 'Gas',
    color: 'var(--warning-600)',
  },
  {
    key: 'water',
    label: 'Agua',
    color: 'var(--info-600)',
  },
] as const;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionHeader = styled.div`
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.25rem;
  color: var(--text-primary);
  font-size: 1.125rem;
  line-height: var(--line-height-tight);
`;

const SectionDescription = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const CostCard = styled(Card)`
  padding: 1.25rem;
`;

const CostLabel = styled.h3`
  margin: 0 0 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
`;

const CostValue = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

const CostCopy = styled.p`
  margin: 0.5rem 0 0;
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

const ChartCard = styled(Card)`
  min-height: 360px;
  padding: 1.25rem;
`;

const ChartTitle = styled.h3`
  margin: 0 0 1rem;
  color: var(--text-primary);
  font-size: 1rem;
`;

const StateMessage = styled.div`
  display: flex;
  min-height: 240px;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  text-align: center;
`;

const DataNote = styled.p`
  margin: 0.75rem 0 0;
  color: var(--warning-700);
  font-size: 0.8rem;
`;

const TooltipBox = styled.div`
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface);
  box-shadow: var(--shadow-md);
`;

const TooltipTitle = styled.p`
  margin: 0 0 0.5rem;
  color: var(--text-primary);
  font-weight: 600;
`;

interface CostTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{
    dataKey?: string;
    value?: number;
    color?: string;
  }>;
  formatter: Intl.NumberFormat;
}

const CostTooltip: React.FC<CostTooltipProps> = ({
  active,
  label,
  payload,
  formatter,
}) => {
  if (!active || !payload?.length) return null;

  return (
    <TooltipBox>
      <TooltipTitle>{label}</TooltipTitle>
      {payload.map((entry) => {
        const config = utilityConfigs.find(
          (item) => item.key === entry.dataKey,
        );
        if (!config || typeof entry.value !== 'number') return null;

        return (
          <div key={config.key} style={{ color: entry.color }}>
            {config.label}: {formatter.format(entry.value)} / plato
          </div>
        );
      })}
    </TooltipBox>
  );
};

export const UtilityCostPerDishSection: React.FC<
  UtilityCostPerDishSectionProps
> = ({
  data,
  currency,
  loading = false,
  error,
  missingCostRecords = 0,
  excludedCurrencyRecords = 0,
}) => {
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }),
    [currency],
  );

  const summary = useMemo(
    () => calculateUtilityCostPerDishSummary(data),
    [data],
  );

  const trendData = useMemo<CostTrendPoint[]>(
    () =>
      data
        .map((month) => ({
          date: `${month.year}-${String(month.month).padStart(2, '0')}`,
          formattedDate: month.month_name,
          electricity: month.electricity_cost_per_dish ?? null,
          water: month.water_cost_per_dish ?? null,
          gas: month.gas_cost_per_dish ?? null,
        }))
        .filter((point) =>
          utilityConfigs.some(
            (config) => typeof point[config.key] === 'number',
          ),
        ),
    [data],
  );

  const hasSummary = utilityConfigs.some(
    (config) => summary[config.key] !== null,
  );

  return (
    <Section aria-labelledby="utility-cost-title">
      <SectionHeader>
        <SectionTitle id="utility-cost-title">Costo por plato</SectionTitle>
        <SectionDescription>
          Incidencia de luz, gas y agua en el costo de cada plato servido.
        </SectionDescription>
      </SectionHeader>

      {loading ? (
        <StateMessage>
          <LoadingSpinner size="md" />
        </StateMessage>
      ) : error ? (
        <StateMessage role="alert">{error}</StateMessage>
      ) : !hasSummary ? (
        <StateMessage>
          Registra costos y cubiertos para calcular estos indicadores.
        </StateMessage>
      ) : (
        <>
          <CardsGrid>
            {utilityConfigs.map((config) => {
              const value = summary[config.key];
              return (
                <CostCard key={config.key}>
                  <CostLabel>{config.label}</CostLabel>
                  <CostValue>
                    {value === null ? 'Sin datos' : formatter.format(value)}
                  </CostValue>
                  <CostCopy>
                    {value === null
                      ? 'Faltan costos o cubiertos para el período.'
                      : `Tu consumo de ${config.label.toLowerCase()} costó ${formatter.format(value)} por plato.`}
                  </CostCopy>
                </CostCard>
              );
            })}
          </CardsGrid>

          <ChartCard>
            <ChartTitle>Tendencia mensual de costo por plato</ChartTitle>
            <ResponsiveContainer width="100%" height={290}>
              <LineChart
                data={trendData}
                margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
              >
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="formattedDate"
                  stroke="var(--text-secondary)"
                  tickLine={false}
                />
                <YAxis
                  stroke="var(--text-secondary)"
                  tickLine={false}
                  tickFormatter={(value: number) => formatter.format(value)}
                />
                <Tooltip content={<CostTooltip formatter={formatter} />} />
                <Legend wrapperStyle={{ paddingTop: 8, fontSize: 12 }} />
                {utilityConfigs.map((config) => (
                  <Line
                    key={config.key}
                    type="monotone"
                    dataKey={config.key}
                    name={config.label}
                    connectNulls={false}
                    stroke={config.color}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}

      {(missingCostRecords > 0 || excludedCurrencyRecords > 0) && (
        <DataNote role="status">
          {missingCostRecords > 0 &&
            `${missingCostRecords} factura(s) no tienen costo. `}
          {excludedCurrencyRecords > 0 &&
            `${excludedCurrencyRecords} factura(s) con otra moneda fueron excluidas.`}
        </DataNote>
      )}
    </Section>
  );
};
